/**
 * InteractiveIssueView Component
 * Displays the full issue content in a readable scrollable view with interactive elements
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getIssueData } from '../../utils/markdownParser';
import { updateIssueProgress, getIssueProgress } from '../../utils/storageUtils';
import { downloadJSON, openPrintView } from '../../utils/exportUtils';
import ToolModal from './ToolModal';

// Loading Spinner Component (local version)
const LoadingSpinner = () => (
  <div className="flex items-center justify-center p-4">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
    <span className="ml-3 text-gray-600">Loading...</span>
  </div>
);

const InteractiveIssueView = ({ issueId, issueName, onBack, onComplete }) => {
  const [issueData, setIssueData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showToolModal, setShowToolModal] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [reflections, setReflections] = useState({});
  const [completedSections, setCompletedSections] = useState({});

  // Load issue data
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const data = await getIssueData(issueId);
        if (data) {
          setIssueData(data);
          
          // Load saved progress
          const savedProgress = getIssueProgress(issueId);
          if (savedProgress && savedProgress.sections) {
            setReflections(savedProgress.sections.reflections || {});
            setCompletedSections(savedProgress.sections.completed || {});
          }
        }
      } catch (error) {
        console.error('Error loading issue data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [issueId]);

  // Handle reflection input change
  const handleReflectionChange = useCallback((sectionId, value) => {
    const newReflections = { ...reflections, [sectionId]: value };
    setReflections(newReflections);
    
    // Save to localStorage
    updateIssueProgress(issueId, {
      sectionId: 'reflections',
      reflections: newReflections
    });
  }, [issueId, reflections]);

  // Handle section completion
  const handleSectionComplete = useCallback((sectionId, completed) => {
    const newCompleted = { ...completedSections, [sectionId]: completed };
    setCompletedSections(newCompleted);
    
    updateIssueProgress(issueId, {
      sectionId: 'completed',
      completed: newCompleted
    });
  }, [issueId, completedSections]);

  // Handle tool click
  const handleToolClick = useCallback((tool) => {
    setSelectedTool(tool);
    setShowToolModal(true);
  }, []);

  // Handle export
  const handleExport = useCallback((type) => {
    if (type === 'json') {
      downloadJSON(issueId, issueName || issueData?.title || issueId);
    } else if (type === 'print') {
      const responses = getIssueProgress(issueId) || { sections: {} };
      openPrintView(issueId, issueData, responses);
    }
  }, [issueId, issueName, issueData]);

  // Handle completion
  const handleComplete = useCallback(() => {
    // Mark all sections as complete
    updateIssueProgress(issueId, {
      sectionId: 'all-complete',
      isComplete: true
    });
    if (onComplete) {
      onComplete();
    }
  }, [issueId, onComplete]);

  // Render markdown-like content with bold support
  const renderContent = (content, sectionId) => {
    if (!content) return null;
    
    // Split content into paragraphs
    const paragraphs = content.split('\n\n').filter(p => p.trim());
    
    return paragraphs.map((para, idx) => {
      // Handle bold text - split by **text** pattern
      if (para.includes('**')) {
        // Split by bold pattern, keeping the text
        const parts = para.split(/(\*\*[^*]+\*\*)/g);
        return (
          <p key={idx} className="mb-4">
            {parts.map((part, partIdx) => {
              // Check if this is a bold section
              if (part.startsWith('**') && part.endsWith('**')) {
                // Remove the ** wrapper and render as bold
                const boldText = part.slice(2, -2);
                return <strong key={partIdx} className="text-indigo-700 font-semibold">{boldText}</strong>;
              }
              return part;
            })}
          </p>
        );
      }
      
      return <p key={idx} className="mb-4">{para}</p>;
    });
  };
  
  // Render title with bold support (for handling ### and #### headers)
  const renderTitle = (title) => {
    if (!title) return null;
    
    // Strip trailing *** from title (e.g., "Worry ***")
    let cleanTitle = title.replace(/\s*\*+$/, '').trim();
    
    // Handle bold text in titles
    if (cleanTitle.includes('**')) {
      const parts = cleanTitle.split(/(\*\*[^*]+\*\*)/g);
      return (
        <span>
          {parts.map((part, partIdx) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              const boldText = part.slice(2, -2);
              return <strong key={partIdx} className="text-indigo-700 font-semibold">{boldText}</strong>;
            }
            return part;
          })}
        </span>
      );
    }
    
    return cleanTitle;
  };

  // Find and render tool references in content
  const renderContentWithToolLinks = (content, sectionId) => {
    if (!content) return null;
    
    // Split content by tool references
    const parts = content.split(/(\[T-\d+:[^\]]+\])/);
    
    return parts.map((part, idx) => {
      const toolMatch = part.match(/\[T-(\d+):([^\]]+)\]/);
      if (toolMatch) {
        return (
          <button
            key={idx}
            onClick={() => handleToolClick({ id: `T-${toolMatch[1]}`, name: toolMatch[2] })}
            className="inline-flex items-center gap-1 px-2 py-1 mx-1 text-sm bg-indigo-100 text-indigo-700 rounded-full hover:bg-indigo-200 transition-colors"
          >
            {part}
          </button>
        );
      }
      return <span key={idx}>{part}</span>;
    });
  };

  // Check if a section is an intervention step (starts with a number like "1.", "2.")
  const isInterventionStep = (title) => {
    return /^\d+\.\s/.test(title);
  };

  // Render substep content with tool links support
  const renderSubstepContent = (content, substepId) => {
    if (!content) return null;
    
    // Split by tool references and render each part
    const parts = content.split(/(\[T-\d+:[^\]]+\])/);
    
    return (
      <div className="prose prose-sm max-w-none mb-3">
        {parts.map((part, idx) => {
          const toolMatch = part.match(/\[T-(\d+):([^\]]+)\]/);
          if (toolMatch) {
            return (
              <button
                key={idx}
                onClick={() => handleToolClick({ id: `T-${toolMatch[1]}`, name: toolMatch[2] })}
                className="inline-flex items-center gap-1 px-2 py-1 mx-1 text-sm bg-indigo-100 text-indigo-700 rounded-full hover:bg-indigo-200 transition-colors"
              >
                {part}
              </button>
            );
          }
          // Handle bold text within content
          if (part.includes('**')) {
            const boldParts = part.split(/(\*\*[^*]+\*\*)/g);
            return (
              <span key={idx}>
                {boldParts.map((bp, bpIdx) => {
                  if (bp.startsWith('**') && bp.endsWith('**')) {
                    return <strong key={bpIdx} className="text-amber-900 font-semibold">{bp.slice(2, -2)}</strong>;
                  }
                  return bp;
                })}
              </span>
            );
          }
          return <span key={idx}>{part}</span>;
        })}
      </div>
    );
  };

  // Render substeps with reflection inputs
  const renderSubsteps = (substeps, sectionId) => {
    if (!substeps || substeps.length === 0) return null;
    
    return substeps.map((substep) => {
      const substepKey = `${sectionId}-${substep.id}`;
      
      return (
        <div key={substep.id} className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
          {/* Substep Title (bold text from markdown) */}
          <h4 className="text-md font-semibold text-amber-800 mb-2">
            {renderTitle(substep.title)}
          </h4>
          
          {/* Substep Content with tool links */}
          {substep.content && renderSubstepContent(substep.content, substep.id)}
          
          {/* One-line reflection input for substep */}
          <div className="mt-2">
            <input
              type="text"
              value={reflections[substepKey] || ''}
              onChange={(e) => handleReflectionChange(substepKey, e.target.value)}
              placeholder="Your answer..."
              className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white"
            />
          </div>
        </div>
      );
    });
  };

  // Render a section with its content
  const renderSection = (section, index) => {
    const isCompleted = completedSections[section.id];
    const isInteractive = isInterventionStep(section.title);
    const substeps = section.substeps || [];
    
    if (!isInteractive) {
      // Display-only section (intro, example, etc.)
      return (
        <div
          key={section.id}
          className="mb-6 p-4 bg-white rounded-lg border border-gray-200"
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            {renderTitle(section.title)}
          </h3>
          <div className="prose prose-sm max-w-none">
            {renderContentWithToolLinks(section.content, section.id)}
          </div>
        </div>
      );
    }
    
    // Interactive section (intervention steps)
    return (
      <div
        key={section.id}
        className={`mb-6 p-4 rounded-lg border ${
          isCompleted ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
        }`}
      >
        {/* Section Header */}
        <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">
            {renderTitle(section.title)}
          </h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isCompleted || false}
              onChange={(e) => handleSectionComplete(section.id, e.target.checked)}
              className="w-4 h-4 text-green-600 border-green-300 rounded focus:ring-green-500"
            />
            <span className="text-sm text-gray-600">Completed</span>
          </label>
        </div>
        
        {/* Section Content */}
        <div className="prose prose-sm max-w-none mb-4">
          {renderContentWithToolLinks(section.content, section.id)}
        </div>
        
        {/* Render Substeps with reflection inputs if they exist */}
        {substeps.length > 0 ? (
          renderSubsteps(substeps, section.id)
        ) : (
          /* If no substeps, show a one-line input for the step itself */
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <input
              type="text"
              value={reflections[section.id] || ''}
              onChange={(e) => handleReflectionChange(section.id, e.target.value)}
              placeholder="Your answer for this step..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!issueData) {
    return (
      <div className="bg-white rounded-lg p-4 shadow-sm border border-red-200">
        <p className="text-red-600">Error loading issue content. Please try again.</p>
        <button 
          onClick={onBack}
          className="mt-3 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-indigo-600 text-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">{renderTitle(issueData.title)}</h2>
            <p className="text-indigo-200 text-sm">Issue ID: {issueId}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleExport('json')}
              className="px-3 py-1.5 bg-indigo-700 hover:bg-indigo-800 rounded text-sm"
            >
              Export
            </button>
            <button
              onClick={() => handleExport('print')}
              className="px-3 py-1.5 bg-indigo-700 hover:bg-indigo-800 rounded text-sm"
            >
              Print
            </button>
          </div>
        </div>
      </div>

      {/* Content - Scrollable */}
      <div className="max-h-[60vh] overflow-y-auto p-6">
        {/* Intro Section */}
        <div className="mb-6 p-4 bg-indigo-50 rounded-lg">
          <h3 className="text-lg font-semibold text-indigo-900 mb-2">
            {renderTitle(issueData.sections[0]?.title) || 'What is this really about?'}
          </h3>
          <div className="prose prose-sm">
            {renderContent(issueData.sections[0]?.content || '')}
          </div>
        </div>
        
        {/* Real Life Example */}
        {issueData.sections[1] && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              {renderTitle(issueData.sections[1].title)}
            </h3>
            <div className="prose prose-sm">
              {renderContent(issueData.sections[1].content)}
            </div>
          </div>
        )}
        
        {/* Intervention Section and its subsections */}
        {issueData.sections.slice(2).map((section, idx) => renderSection(section, idx + 2))}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
        <button
          onClick={onBack}
          className="px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          ← Return to Chatbot
        </button>
        <button
          onClick={handleComplete}
          className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
        >
          Complete Issue
        </button>
      </div>

      {/* Tool Modal */}
      {showToolModal && selectedTool && (
        <ToolModal 
          tool={selectedTool}
          onClose={() => {
            setShowToolModal(false);
            setSelectedTool(null);
          }}
        />
      )}
    </div>
  );
};

export default InteractiveIssueView;